import { useState } from "react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

// ============================================================================
// ApproveButton — Phase G approval-gate (pt.35). Stage 4 owners
// (Brett + Nathanael, per stage_4_interviewers role) grant approvals
// for actions the executor (Juliana) will perform. Server enforces
// the workflow gates; this modal is just an ergonomic capture of
// approver notes.
// ============================================================================

const TYPE_META = {
  staff_meeting_invite: {
    label: "Approve staff meeting invite",
    dialogTitle: "Approve staff meeting invite",
    description: (
      <>
        Grants Juliana permission to send the Monday staff-meeting shadow-event
        invite to this candidate.
        <br />
        <span className="font-medium">
          Also reassigns ownership from you to Juliana.
        </span>{" "}
        Once approved, this decision is immutable.
      </>
    ),
    submitText: "Approve invite",
  },
  offer_extension: {
    label: "Approve offer extension",
    dialogTitle: "Approve offer extension",
    description: (
      <>
        Grants Juliana permission to run the Extend offer flow (compensation
        type, dates, terms) for this candidate.
        <br />
        Requires a staff-meeting-invite approval to already exist. Once
        approved, this decision is immutable.
      </>
    ),
    submitText: "Approve offer",
  },
};

export default function ApproveButton({ candidate, type, onApproved }) {
  const meta = TYPE_META[type];
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");

  async function submit() {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/hiring/candidates/${candidate.id}/approve`,
        { type, notes: notes.trim() },
      );
      toast.success(`${meta.label} — recorded`);
      setOpen(false);
      setNotes("");
      onApproved?.(data);
    } catch (e) {
      toast.error(extractApiError(e) || "Approval failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid={`hiring-candidate-approve-${type}-btn`}
      >
        <ShieldCheck className="w-4 h-4 mr-1.5" /> {meta.label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid={`hiring-candidate-approve-${type}-modal`}
        >
          <DialogHeader>
            <DialogTitle>{meta.dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-[#4b5563] bg-[#f3f4f6] border border-[#e5e7eb] rounded p-3 mb-2">
            {meta.description}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
              Notes (optional — captured on the approval record and timeline)
            </label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Interview passed, comfortable moving forward"
              data-testid={`hiring-candidate-approve-${type}-notes`}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid={`hiring-candidate-approve-${type}-cancel`}
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid={`hiring-candidate-approve-${type}-submit`}
            >
              {saving ? "Approving…" : meta.submitText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
